import { Button, Flex, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@/components/shared/dialog";
import Select from "@/components/shared/select";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import type { RegressionSet } from "@/v2/entities/regression/Regression.model";
import type { AllRegressionTests } from "@/v2/entities/regression/Regression.model";
import { useQuery } from "react-query";
import { formatRegressionRunMethodLabel } from "@/routes/regression-tests/regressionTestRunMethodLabel";

interface CompareRegressionTestsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regressionSets: RegressionSet[] | undefined;
}

function formatTestOptionLabel(test: AllRegressionTests) {
  const suffix = test.id.slice(-8);
  const method = formatRegressionRunMethodLabel(test.runMethod);
  const date = new Date(test.createdAt).toLocaleString();
  return `${suffix} · ${method} · ${date}`;
}

export function CompareRegressionTestsDialog({
  open,
  onOpenChange,
  regressionSets,
}: CompareRegressionTestsDialogProps) {
  const navigate = useNavigate();
  const { getAllRegressionTests } = useRegressionApi();
  const { data: allTests } = useQuery({
    queryKey: ["regression-tests"],
    queryFn: getAllRegressionTests,
    enabled: open,
  });

  const [setId, setSetId] = useState("");
  const [test1Id, setTest1Id] = useState("");
  const [test2Id, setTest2Id] = useState("");

  useEffect(() => {
    if (!open) {
      setSetId("");
      setTest1Id("");
      setTest2Id("");
    }
  }, [open]);

  const eligibleTests = useMemo(() => {
    if (!allTests || !setId) return [];
    return allTests.filter((t) => t.regressionSetId === setId);
  }, [allTests, setId]);

  const sortedEligibleTests = useMemo(() => {
    return [...eligibleTests].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [eligibleTests]);

  const testOptions = useMemo(
    () =>
      sortedEligibleTests.map((t) => ({
        value: t.id,
        label: formatTestOptionLabel(t),
      })),
    [sortedEligibleTests]
  );

  const setOptions = useMemo(
    () =>
      regressionSets?.map((s) => ({
        value: s.id,
        label: `${s.name} (${s.id.slice(0, 8)}…)`,
      })) ?? [],
    [regressionSets]
  );


  const handleCompare = useCallback(() => {
    if (!setId || !test1Id || !test2Id || test1Id === test2Id) return;
    navigate(`/automation-center/compare/regression-sets/${setId}/regression-tests/${test1Id}/${test2Id}`);
    onOpenChange(false);
  }, [setId, test1Id, test2Id, navigate, onOpenChange]);

  const handleSetChange = useCallback((value: string) => {
    setSetId(value);
    setTest1Id("");
    setTest2Id("");
  }, []);

  const canCompare =
    Boolean(setId && test1Id && test2Id) && test1Id !== test2Id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium" className="p-4">
        <Dialog.Title>Compare test results</Dialog.Title>
        <Flex direction="column" gap="3" mt="3">
          <Text size="2" color="gray">
            Choose a regression set, then pick any two test runs from that set.
          </Text>
          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Regression set
            </Text>
            <Select
              value={setId}
              onChange={handleSetChange}
              options={setOptions}
            />
          </label>
          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              First regression test
            </Text>
            <Select
              value={test1Id}
              onChange={setTest1Id}
              options={testOptions}
            />
          </label>
          <label>
            <Text as="div" size="2" mb="1" weight="bold">
              Second regression test
            </Text>
            <Select
              value={test2Id}
              onChange={setTest2Id}
              options={testOptions}
            />
          </label>
          <Flex gap="2" justify="end" mt="2">
            <Button variant="soft" color="gray" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              color="gray"
              highContrast
              onClick={handleCompare}
              disabled={!canCompare}
            >
              Compare
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
